import React, { useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import { useAuth } from './AuthContext';
import Mark from './components/Mark';
import FleetSidebar from './components/FleetSidebar';
import LanguageSelector from './components/LanguageSelector';
import { useFleet } from './FleetContext';
import { useI18n } from './i18n';
import { PROTOTYPES } from './prototypes';
import { colors, font, type, layout } from './theme/tokens';

type Props = {
  children: React.ReactNode;
  /** Two-digit prototype index, e.g. '02'. */
  protoId: string;
  /** i18n key for the page title, e.g. 'proto.02.title'. */
  titleKey: string;
  /**
   * Content canvas. 'dark' is the legacy white-on-navy styling; 'light' is the
   * token palette. Defaults to 'light' now that all four pages are converted.
   */
  surface?: 'light' | 'dark';
};

// ── Tab bar ──────────────────────────────────────────────────────────────────

const PrototypeTabs: React.FC<{ activePath: string }> = ({ activePath }) => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { unitId, sidebarOpen, toggleSidebar } = useFleet();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);

  // On a narrow screen the strip scrolls; keep the active tab in view.
  useEffect(() => {
    const el = activeRef.current;
    const scroller = scrollerRef.current;
    if (!el || !scroller) return;
    const elLeft = el.offsetLeft;
    const elRight = elLeft + el.offsetWidth;
    if (elLeft < scroller.scrollLeft || elRight > scroller.scrollLeft + scroller.clientWidth) {
      scroller.scrollTo({ left: elLeft - 16, behavior: 'smooth' });
    }
  }, [activePath]);

  return (
    <Box sx={{
      borderBottom: `1px solid ${colors.border}`,
      backgroundColor: colors.bg,
      position: 'sticky',
      top: layout.topbarHeight,
      zIndex: 99,
    }}>
      <Box sx={{
        maxWidth: layout.maxWidthWide,
        mx: 'auto',
        px: layout.gutter,
        display: 'flex',
        alignItems: 'stretch',
        gap: 1,
      }}>
        {/* Fleet menu toggle */}
        <Button
          onClick={toggleSidebar}
          disableRipple
          aria-expanded={sidebarOpen}
          title={sidebarOpen ? t('fleet.collapse') : t('fleet.expand')}
          sx={{
            flexShrink: 0,
            alignSelf: 'center',
            px: 1.25,
            py: 0.75,
            minWidth: 0,
            borderRadius: layout.radius,
            textTransform: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 0.9,
            border: `1px solid ${sidebarOpen ? colors.blue : colors.border}`,
            backgroundColor: sidebarOpen ? colors.blue : 'transparent',
            color: sidebarOpen ? colors.onDark : colors.inkMuted,
            '&:hover': {
              backgroundColor: sidebarOpen ? colors.blueDeep : colors.blueWash,
              borderColor: colors.blue,
              color: sidebarOpen ? colors.onDark : colors.blue,
            },
          }}
        >
          {/* Three-bar glyph, drawn rather than imported */}
          <Box component="span" sx={{
            display: 'flex', flexDirection: 'column', gap: '3px', flexShrink: 0,
          }}>
            {[0, 1, 2].map(i => (
              <Box key={i} component="span" sx={{
                width: 13, height: 2, borderRadius: '1px',
                backgroundColor: sidebarOpen ? colors.accent : 'currentColor',
              }} />
            ))}
          </Box>
          <Box component="span" sx={{ fontFamily: font.mono, fontSize: '11px', fontWeight: 700 }}>
            {unitId}
          </Box>
        </Button>

        <Box sx={{ width: '1px', my: 1.5, backgroundColor: colors.border, flexShrink: 0 }} />

        <Box
          ref={scrollerRef}
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            gap: 0.5,
            flex: 1,
            minWidth: 0,
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          <Button
            onClick={() => navigate('/')}
            disableRipple
            sx={{
              flexShrink: 0,
              px: 1.25,
              py: 1.5,
              minWidth: 0,
              borderRadius: 0,
              textTransform: 'none',
              color: colors.inkMuted,
              borderBottom: '3px solid transparent',
              fontFamily: font.sans,
              fontSize: '13px',
              fontWeight: 500,
              '&:hover': { backgroundColor: 'transparent', color: colors.ink },
            }}
          >
            ← {t('app.allDemos')}
          </Button>

          {PROTOTYPES.map(p => {
            const active = p.path === activePath;
            const disabled = p.status !== 'available';
            return (
              <Button
                key={p.num}
                ref={active ? activeRef : undefined}
                onClick={() => !disabled && navigate(p.path)}
                disabled={disabled}
                disableRipple
                sx={{
                  flexShrink: 0,
                  px: 1.75,
                  py: 1.5,
                  minWidth: 0,
                  borderRadius: 0,
                  textTransform: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: active ? colors.ink : colors.inkMuted,
                  borderBottom: `3px solid ${active ? colors.accent : 'transparent'}`,
                  transition: 'color .15s, border-color .15s',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: colors.ink,
                    borderBottomColor: active ? colors.accent : colors.border,
                  },
                  '&.Mui-disabled': { color: colors.inkGhost },
                }}
              >
                <Box component="span" sx={{
                  fontFamily: font.mono,
                  fontSize: '11px',
                  fontWeight: 500,
                  color: active ? colors.accentDeep : 'inherit',
                  opacity: active ? 1 : 0.7,
                }}>
                  {p.num}
                </Box>
                <Box component="span" sx={{
                  fontFamily: font.sans,
                  fontSize: '13px',
                  fontWeight: active ? 700 : 500,
                  whiteSpace: 'nowrap',
                }}>
                  {t(`proto.${p.num}.short`)}
                </Box>
              </Button>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

// ── Layout ───────────────────────────────────────────────────────────────────

const PrototypeLayout: React.FC<Props> = ({ children, protoId, titleKey }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { unitId, sidebarOpen } = useFleet();
  const { t } = useI18n();

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: colors.bgAlt,
      fontFamily: font.sans,
      display: 'flex',
      flexDirection: 'column',
    }}>

      {/* ── Topbar ──────────────────────────────────────────────────────── */}
      <Box sx={{
        height: layout.topbarHeight,
        borderBottom: `1px solid ${colors.border}`,
        backgroundColor: colors.barTint,
        position: 'sticky',
        top: 0,
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
      }}>
        <Box sx={{
          width: '100%',
          maxWidth: layout.maxWidthWide,
          mx: 'auto',
          px: layout.gutter,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
            <Box
              onClick={() => navigate('/')}
              role="button"
              tabIndex={0}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/'); }
              }}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                borderRadius: layout.radius,
                outline: 'none',
                transition: 'opacity .15s ease',
                '&:hover, &:focus-visible': { opacity: 0.7 },
              }}
            >
              <Mark />
            </Box>

            <Box sx={{ width: '1px', height: 18, backgroundColor: colors.borderStrong, flexShrink: 0 }} />

            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, minWidth: 0 }}>
              <Typography sx={{
                ...type.meta,
                color: colors.accentDeep,
                flexShrink: 0,
                display: { xs: 'none', sm: 'block' },
              }}>
                {protoId} · {unitId}
              </Typography>
              <Typography sx={{
                fontFamily: font.sans,
                fontSize: '14px',
                fontWeight: 500,
                color: colors.ink,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {t(titleKey)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
            <LanguageSelector />

            <Typography sx={{
              ...type.meta,
              color: colors.inkFaint,
              display: { xs: 'none', lg: 'block' },
            }}>
              {user?.email}
            </Typography>

            <Button
              onClick={signOut}
              disableRipple
              sx={{
                fontFamily: font.sans,
                fontSize: '13px',
                fontWeight: 500,
                color: colors.inkMuted,
                textTransform: 'none',
                px: 1.75,
                py: 0.75,
                minWidth: 0,
                backgroundColor: colors.bg,
                border: `1px solid ${colors.borderStrong}`,
                borderRadius: layout.radius,
                '&:hover': {
                  backgroundColor: colors.bg,
                  color: colors.blue,
                  borderColor: colors.blue,
                },
              }}
            >
              {t('app.signOut')}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ── Prototype tabs ──────────────────────────────────────────────── */}
      <PrototypeTabs activePath={location.pathname} />

      {/* ── Content ─────────────────────────────────────────────────────── */}
      <Box sx={{
        flex: 1,
        width: '100%',
        maxWidth: layout.maxWidthWide,
        mx: 'auto',
        px: layout.gutter,
        py: { xs: 3, md: 5 },
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'flex-start',
        gap: { xs: 3, md: 4 },
      }}>
        {sidebarOpen && <FleetSidebar />}
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default PrototypeLayout;
